import { ModalManager } from '../modalManager/_modalManager';
import { v4 as uuidv4 } from 'uuid';

/***
 * このモジュールは、.ist-js__modal 要素群を探索し、
 * その中の .ist-js__modalButton がクリックされた際に、
 * 対応する .ist-js__modalContent の内容をモーダルとして表示します。
 *
 * Modal モジュールを呼び出して利用してください。
 *
 * 使用例:
 *
 * import { Modal } from './Modal';
 *
 * const modal = Modal.getInstance();
 *
 * 追加仕様01
 * data-class で、モーダルに任意のクラスを追加する
 *
 * 追加仕様02
 * idが指定されている場合は、.ist-js__modal 配下に存在しない.ist-js__modalContentでも表示可能とする
 *
 */
export interface ModalOptions {
  content: string;
  modalId: string;
  modalAriaLabel?: string;
  triggerElement?: HTMLElement;
  dataClass?: string | null;
  onClose?: () => void;
}

interface ModalConfig {
  log?: boolean; // ログ出力を制御
}

export class Modal {
  private modalManager: ModalManager;
  private logEnabled: boolean;

  constructor(config: ModalConfig = {}) {
    this.modalManager = new ModalManager();
    this.logEnabled = config.log ?? false; // logが渡されなければデフォルトでfalse
    this.initialize();
  }

  /**
   * ログを出力する（logEnabledがtrueの場合のみ）
   */
  private log(message: string): void {
    if (this.logEnabled) {
      console.log(`[モーダル]: ${message}`);
    }
  }

  private initialize(): void {
    const modals = document.querySelectorAll(
      '.ist-js__modal'
    ) as NodeListOf<HTMLElement>;
    this.initializeModals(modals);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          const newModals = Array.from(mutation.addedNodes).filter(
            (node): node is HTMLElement =>
              node instanceof HTMLElement && node.matches('.ist-js__modal')
          );
          this.initializeModals(newModals);
        }
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
    this.log('モーダルの初期化とオブザーバーの設定が完了しました。');
  }

  private initializeModals(
    modals: NodeListOf<HTMLElement> | HTMLElement[]
  ): void {
    modals.forEach((modalContainer) => {
      const openButton = modalContainer.querySelector(
        '.ist-js__modalButton'
      ) as HTMLAnchorElement | HTMLButtonElement | null;

      let contentElement: HTMLElement | null = null;

      if (openButton) {
        const href = openButton.getAttribute('href');
        if (href && href.startsWith('#')) {
          const modalContentId = href.substring(1);
          contentElement = document.getElementById(modalContentId);
        }

        if (!contentElement) {
          contentElement = modalContainer.querySelector(
            '.ist-js__modalContent'
          ) as HTMLDivElement | null;
        }

        if (contentElement) {
          if (!openButton.hasAttribute('data-listener-added')) {
            let modalId = contentElement.getAttribute('id');
            if (!modalId) {
              modalId = `ist-js__modal-generated-id-${uuidv4()}`;
              contentElement.setAttribute('id', modalId);
            }

            const modalLabel =
              openButton.getAttribute('data-aria-label') ||
              'モーダルウィンドウ';

            const dataClass = openButton.getAttribute('data-class');
            if (dataClass) {
              contentElement.classList.add(dataClass);
            }

            openButton.addEventListener('click', (event) => {
              event.preventDefault();

              this.modalManager.showModal({
                content: contentElement!.innerHTML,
                modalId: modalId as string,
                modalAriaLabel: modalLabel,
                triggerElement: openButton,
                dataClass: dataClass,
                onClose: () => {
                  this.log(`モーダル（ID: ${modalId}）が閉じられました。`);
                },
              });
              this.log(`モーダル（ID: ${modalId}）が開かれました。`);
            });

            openButton.setAttribute('data-listener-added', 'true');
            this.log(
              `モーダル（ID: ${modalId}）のボタンにイベントリスナーを追加しました。`
            );
          }
        }
      }
    });
  }
}
